import React, { useState, useEffect } from 'react';
import GoogleMapReact from 'google-map-react';
import { Popover, ArrowContainer } from 'react-tiny-popover';
import { useFormik } from 'formik';
import Link from 'next/link';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { stringify } from 'query-string';
import { isEmpty } from 'lodash';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';

import { NEXT_PUBLIC_GOOGLE_MAPS_KEY } from '@/app-configuration';
import API from '@utils/api';
import { Layout, Tabs } from '@components';
import Container from '@components/Container';
import Breadcrumbs from '@components/Breadcrumbs';
import Button from '@components/Button';
import { checkEnabled, checkValue } from '@components/OptionalFeature';
import { getDealerTiming } from '@helpers/dealer';
import { getLanguageId, translateDay, translateFRDepartment, formatTime } from '@/helpers/language';
import { formatPostData } from '@/services/postService';
import { PageContext } from '@contexts';
import { apiClient } from '@/http-client';
import benberglogo from '../../../public/assets/BenBerg_Logo.svg';
import styles from './Dealer.module.scss';

export default function DealerPage(props) {
  const router = useRouter();
  const { locale } = router;
  const {
    route,
    query: { product, isUsed }
  } = router;

  const location = props?.data?.location;
  const [timing, setTiming] = useState({});

  const defaultProps = {
    center: {
      lat: +location?.latitudes || 43.71579,
      lng: +location?.longitude || -79.52898
    },
    zoom: 11
  };

  useEffect(() => {
    if (location && Array.isArray(location.hours)) {
      let dealerTimings = getDealerTiming(location.hours);
      setTiming(dealerTimings);
    }
  }, [location]);

  const MapIcon = ({ text }) => (
    <div className={styles.Marker}>
      <img src="../../../../Orange_Pin.svg" />
    </div>
  );

  const dealerInfo = props?.data?.DisplayOptions.find((x) => x.Name === 'Dealer Info');
  
  // show buttonText
  const showContactDealerButtonText = checkEnabled(dealerInfo.DisplayOptions, 'ContactDealerButtonText');

  // buttonText
  const contactDealerButtonText = checkValue(dealerInfo.DisplayOptions, 'ContactDealerButtonText', locale);

  // show GetDirectionsLinkText
  const showGetDirectionsLinkText = checkEnabled(dealerInfo.DisplayOptions, 'GetDirectionsLinkText');

  // GetDirectionsLinkText
  const getDirectionsLinkText = checkValue(dealerInfo.DisplayOptions, 'GetDirectionsLinkText', locale);

  // show HoursSectionTitle
  const showHoursSectionTitle = checkEnabled(dealerInfo.DisplayOptions, 'HoursSectionTitle');

  // HoursSectionTitle text
  const hoursSectionTitle = checkValue(dealerInfo.DisplayOptions, 'HoursSectionTitle', locale);

  const locationAddress = [location.address1, location.address2, location.city, location.province, location.postalCode]
    .filter(value => !isEmpty(value))
    .join(' ');

  return (
    <PageContext.Provider value={props?.data}>
      <Head>
        {location && (
          <>
            <meta property="og:title" content={location.name} />
            <meta property="og:description" content={locationAddress} />
            <meta property="og:image" content={`${location.logo}`} />
          </>
        )}
      </Head>
      <Layout>
        {location && Object.keys(location).length > 0 && (
          <Container>
            <div className={styles.DealerPage}>
              <div className={styles.breadcrumbs__wrapper}>
                <div className={styles.breadcrumbs__wrapper__item}>
                  <Link href={`/`} prefetch={false}>
                    <a>{locale == 'fr' ? "Accuiel" : 'Home'}</a>
                  </Link>
                  <Link href="/find-a-dealer" prefetch={false}>
                    <a>{locale == 'fr' ? 'Localisateur de concessionnaires': 'Find A Dealer'}</a>
                  </Link>
                  <a href="#">{location.name}</a>
                </div>
              </div>
              <div className={styles.DealerTitle}>
                <h1 className={styles.BigTitle}>{location.name}</h1>
                <img className={styles.mainLogo} src={location?.logo} />
              </div>
              <div className={styles.InnerWrapper}>
                <div className={styles.DealerInfo}>
                  {/* <a href={`mailto:${dealer?.email}`} className={styles.contact__dealer__button}><Button animated>{locale==='en' ? 'Contact Dealer' : 'Contacter le concessionnaire'}</Button></a> */}
                  <Link
                    href={`/dealer/contact-a-dealer?dealer=${props?.data?.dealer}${
                      isEmpty({ product, isUsed }) ? '' : '?' + stringify({ product, isUsed })
                    }`}
                    className={styles.contact__dealer__button}
                    prefetch={false}
                  >
                    <Button animated>
                      {showContactDealerButtonText ? contactDealerButtonText : null}
                    </Button>
                  </Link>
                  <div className={styles.linkWrapper}>
                    <div className={styles.link}>
                      <a target="_blank" rel="noreferrer" href={location?.url}>
                        {location?.url}
                      </a>
                    </div>
                    <div className={styles.link}>
                      <a href={`tel:${location?.phone}`}>{location?.phone}</a>
                    </div>
                  </div>
                  <div className={styles.address}>
                    <p>{location?.address1}</p>
                    <p>{location?.address2}</p>
                    <p>
                      {location?.city}, {location?.province}
                    </p>
                    <p>{location?.postalCode}</p>
                  </div>
                  <div className={styles.linkWrapper}>
                    <div className={styles.link}>
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href={`https://www.google.com/maps/search/?api=1&query=${location?.latitudes || 43.71579},${location?.longitude || -79.52898}`}
                      >
                        {showGetDirectionsLinkText ? getDirectionsLinkText : null}
                      </a>
                    </div>
                  </div>
                  <div className={styles.hours}>
                    {timing?.timing?.length > 0 && <h2>{showHoursSectionTitle ? hoursSectionTitle : null} </h2>}
                      {timing?.isSameTime && timing.timing.map((m, i) => (
                        <div className={styles.dayTimeWrapper} key={i}>
                          <div className={styles.day}>{locale == 'fr' ? translateDay(m.day): m.day}</div>&nbsp;
                          <div className={styles.time}>{formatTime(m.open, locale)} {locale == 'fr'? 'à' : 'to'} {formatTime(m.close, locale)}</div>
                        </div>
                      ))}
                    {!timing?.isSameTime && Array.isArray(timing?.timing) && timing?.timing?.length > 0 && (
                      <Tabs>
                        {Array.isArray(timing.timing) &&
                          timing.timing.length > 0 &&
                          timing.timing.map((m, i) => (
                            <Tabs.Tab label={locale == 'fr' ? translateFRDepartment(m.department) : m.department} key={i}>
                              <div className={styles.company__hours__wrapper}>
                                <div className={styles.office__hours}>
                                  {m.item &&
                                    m.item.map((mm, index) => (
                                      <div className={styles.dayTimeWrapper} key={index}>
                                        <div className={styles.day}>{locale == 'fr' ? translateDay(mm.day) : mm.day}</div>
                                        <div className={styles.time}>
                                          {formatTime(mm.open, locale)} {locale == 'fr' ? 'à' : 'to'} {formatTime(mm.close, locale)}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            </Tabs.Tab>
                          ))}
                      </Tabs>
                    )}
                  </div>
                </div>
                <div className={styles.mapContainer}>
                  <GoogleMapReact
                    bootstrapURLKeys={{ key: NEXT_PUBLIC_GOOGLE_MAPS_KEY }}
                    defaultCenter={defaultProps.center}
                    defaultZoom={defaultProps.zoom}
                    center={defaultProps.center}
                    zoom={defaultProps.zoom}
                    yesIWantToUseGoogleMapApiInternals={true}
                  >
                    <MapIcon
                      lat={defaultProps.center.lat}
                      lng={defaultProps.center.lng}
                      // text={productData.company}
                    />
                  </GoogleMapReact>
                </div>
              </div>
            </div>
          </Container>
        )}
      </Layout>
    </PageContext.Provider>
  );
}

export const getServerSideProps = async ({ query, locale }) => {
  const { dealer } = query;
  let location = null;

  const title = 'find-a-dealer';
  const languageId = getLanguageId(locale);

  const { data: post } = await apiClient.get(`/api/Posts/byTitle/${title}`, { params: { languageId } });

  const data = formatPostData(post);

  try {
    location = await API.post('/api/findDealer', dealer) || null;
  } catch (error) {
    return {
      notFound: true,
      redirect: {
        permanent: true,
        destination: '/404'
      }
    };
  }

  data['location'] = location;
  data['dealer'] = dealer;

  return {
    props: {
      data,
      ...(await serverSideTranslations(locale, ['common', 'footer']))
    }
  };
};
